import React, { memo, useCallback, useMemo } from 'react';
import {
  Typography,
  Chip,
  Box,
  IconButton,
  Checkbox,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { formatDateSafe } from '../../utils/dateUtils';
import { Invoice } from '../../types/invoice';
import PDFDownload from '../PDF/PDFDownload';
import czechTranslations from '../../translations/czech';

const statusColors = {
  new: 'default',
  pending: 'warning',
  reviewed: 'info',
  ready_for_payment: 'primary',
  sent_to_bank: 'secondary',
  paid: 'success',
  rejected: 'error'
} as const;

const getStatusLabel = (status: string) => {
  return czechTranslations.invoices.status[status as keyof typeof czechTranslations.invoices.status] || status;
};

// Memoized Status Cell Component
export const OptimizedStatusCell = memo(({ status }: { status: string }) => {
  return (
    <Chip
      label={getStatusLabel(status)}
      color={statusColors[status as keyof typeof statusColors] as any}
      size="small"
      variant="outlined"
      sx={{
        fontSize: '0.75rem',
        height: '24px',
      }}
    />
  );
});

// Memoized Amount Cell Component
export const OptimizedAmountCell = memo(({ amount }: { amount: number }) => {
  const formattedAmount = useMemo(() => {
    if (typeof amount === 'number') {
      return `${amount.toLocaleString('cs-CZ')} Kč`;
    }
    return '0 Kč';
  }, [amount]);

  return (
    <Typography 
      variant="body2" 
      sx={{ 
        fontWeight: 'bold', 
        color: 'primary.main',
        fontSize: '0.875rem',
      }}
    >
      {formattedAmount}
    </Typography>
  );
});

// Memoized Date Cell Component
export const OptimizedDateCell = memo(({ date }: { date: string | Date }) => {
  const formattedDate = useMemo(() => {
    return formatDateSafe(date);
  }, [date]);

  return (
    <Typography 
      variant="body2"
      sx={{ fontSize: '0.875rem' }}
    >
      {formattedDate}
    </Typography>
  );
});

// Memoized Business Unit Cell Component
export const OptimizedBusinessUnitCell = memo(({ businessUnit }: { businessUnit: any }) => {
  if (!businessUnit) {
    return (
      <Chip
        label="Nepřiřazeno"
        color="warning"
        size="small"
        variant="outlined"
        sx={{ fontSize: '0.75rem', height: '24px' }}
      />
    );
  }

  return (
    <Box>
      <Typography 
        variant="body2" 
        sx={{ 
          fontWeight: 'medium',
          fontSize: '0.875rem',
        }}
      >
        {businessUnit.name}
      </Typography>
      {businessUnit.ico && (
        <Typography 
          variant="caption" 
          color="textSecondary"
          sx={{ fontSize: '0.75rem' }}
        >
          IČO: {businessUnit.ico}
        </Typography>
      )}
    </Box>
  );
});

// Memoized Actions Cell Component
export const OptimizedActionsCell = memo(({ 
  invoice, 
  onView, 
  onEdit, 
  onDelete, 
  onAssignCompany 
}: { 
  invoice: Invoice;
  onView: (id: number) => void;
  onEdit: (id: number) => void;
  onDelete: (invoice: Invoice) => void;
  onAssignCompany: (invoice: Invoice) => void;
}) => {
  const handleView = useCallback(() => {
    onView(invoice.id);
  }, [onView, invoice.id]);

  const handleEdit = useCallback(() => {
    onEdit(invoice.id);
  }, [onEdit, invoice.id]);

  const handleDelete = useCallback(() => {
    onDelete(invoice);
  }, [onDelete, invoice]);

  const handleAssignCompany = useCallback(() => {
    onAssignCompany(invoice);
  }, [onAssignCompany, invoice]);

  return (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      <IconButton
        size="small"
        onClick={handleView}
        title="Zobrazit detail"
        sx={{ padding: '4px' }}
      >
        <ViewIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={handleEdit}
        title="Upravit"
        sx={{ padding: '4px' }}
      >
        <EditIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={handleAssignCompany}
        title="Přiřadit firmu"
        color="info"
        sx={{ padding: '4px' }}
      >
        <BusinessIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={handleDelete}
        title="Smazat"
        color="error"
        sx={{ padding: '4px' }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
});

// Memoized Invoice Number Cell Component
export const OptimizedInvoiceNumberCell = memo(({ invoiceNumber }: { invoiceNumber: string }) => {
  return (
    <Typography 
      variant="body2" 
      sx={{ 
        fontWeight: 'bold',
        fontSize: '0.875rem',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {invoiceNumber}
    </Typography>
  );
});

// Memoized Supplier Cell Component
export const OptimizedSupplierCell = memo(({ invoice }: { invoice: Invoice }) => {
  return (
    <Box>
      <Typography 
        variant="body2" 
        sx={{ 
          fontWeight: 'medium',
          fontSize: '0.875rem',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={`${invoice.supplierName}${invoice.supplierICO ? ` IČO: ${invoice.supplierICO}` : ''}`}
      >
        {invoice.supplierName}
      </Typography>
      {invoice.supplierICO && (
        <Typography 
          variant="caption" 
          color="textSecondary"
          sx={{ 
            fontSize: '0.75rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          IČO: {invoice.supplierICO}
        </Typography>
      )}
    </Box>
  );
});

// Memoized Customer Cell Component
export const OptimizedCustomerCell = memo(({ invoice }: { invoice: Invoice }) => {
  return (
    <Box>
      <Typography 
        variant="body2" 
        sx={{ 
          fontWeight: 'medium',
          fontSize: '0.875rem',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={`${invoice.customerName}${invoice.customerICO ? ` IČO: ${invoice.customerICO}` : ''}`}
      >
        {invoice.customerName}
      </Typography>
      {invoice.customerICO && (
        <Typography 
          variant="caption" 
          color="textSecondary"
          sx={{ 
            fontSize: '0.75rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          IČO: {invoice.customerICO}
        </Typography>
      )}
    </Box>
  );
});

// Memoized PDF Download Component
export const OptimizedPDFDownload = memo(({ invoice }: { invoice: Invoice }) => {
  if (!(invoice.pdfData || invoice.pdfFilename)) {
    return null;
  }

  return (
    <PDFDownload
      invoiceId={invoice.id}
      invoiceNumber={invoice.invoiceNumber}
      hasPdf={true}
      size="small"
    />
  );
});

// Memoized Checkbox Component
export const OptimizedCheckbox = memo(({ 
  checked, 
  onChange, 
  title 
}: { 
  checked: boolean;
  onChange: (checked: boolean) => void;
  title?: string;
}) => {
  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.checked);
  }, [onChange]);

  return (
    <Checkbox
      checked={checked}
      onChange={handleChange}
      title={title}
      size="small"
      sx={{ padding: '4px' }}
    />
  );
});

// Memoized ABO Status Cell Component
export const OptimizedABOStatusCell = memo(({ aboFile }: { aboFile: any }) => {
  if (!aboFile) return null;

  const aboStatusLabels = {
    generated: 'Vytvořeno',
    sent: 'Odesláno',
    sent_to_fio: 'Odesláno do FIO',
    error: 'Chyba'
  } as const;

  return (
    <Box>
      <Typography 
        variant="body2" 
        sx={{ 
          fontWeight: 'medium',
          fontSize: '0.875rem',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {aboFile.filename}
      </Typography>
      <Typography 
        variant="caption" 
        color="textSecondary"
        sx={{ fontSize: '0.75rem' }}
      >
        {aboFile.bankType} • {aboStatusLabels[aboFile.status as keyof typeof aboStatusLabels] || aboFile.status}
      </Typography>
    </Box>
  );
});

// Memoized VAT Rendered Date Cell Component
export const OptimizedVATDateCell = memo(({ date }: { date: string | Date | null }) => {
  const formattedDate = useMemo(() => {
    return formatDateSafe(date);
  }, [date]);

  if (!formattedDate) {
    return (
      <Typography 
        variant="body2" 
        color="text.secondary"
        sx={{ fontSize: '0.875rem' }}
      >
        Není zadáno
      </Typography>
    );
  }

  return (
    <Typography 
      variant="body2" 
      color="text.primary"
      sx={{ fontSize: '0.875rem' }}
    >
      {formattedDate}
    </Typography>
  );
});
